/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {PERMISSIONS} from '@agent-assist/common/lib/admin/rbac';
import BuildIcon from '@mui/icons-material/Build';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import {TabContext, TabList, TabPanel} from '@mui/lab';
import {Box, Button, Chip, CircularProgress, Stack, Tab, Typography} from '@mui/material';
import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';
import {AgentAbilities} from './components/AgentAbilities';
import {AgentAbout} from './components/AgentAbout';
import {AgentDocuments} from './components/AgentDocuments';
import {AgentFAQs} from './components/AgentFAQs';
import {AgentSettings} from './components/AgentSettings';
import {useDeleteAgent} from './hooks/useDeleteAgent';
import {useGetAgent} from './hooks/useGetAgent';
import {useGetAgentAbilityTypes} from './hooks/useGetAgentAbilityTypes';
import {useGetAgentActionTypes} from './hooks/useGetAgentActionTypes';
import {useGetVoices} from './hooks/useGetVoices';
import {useUpdateAgent} from './hooks/useUpdateAgent';
import {PageContainer} from '../../components/PageContainer';
import {FeatureFlags, type FeatureFlagSet} from '../../flags';
import {useToast} from '../../hooks/useToast';
import {ROUTES} from '../../routes';
import {useUserStore} from '../../stores/userStore';
import {type Agent, type NewAgent} from '../../types/agent';

const AgentsEditPage = () => {
  const {agentId} = useParams<{agentId: string}>();
  const {fetch, agent, isLoading: isLoadingGet, error: errorGet} = useGetAgent();
  const {update, isLoading: isLoadingUpdate} = useUpdateAgent();
  const {del, isLoading: isLoadingDelete} = useDeleteAgent();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState('about');
  const [localAgent, setLocalAgent] = useState<NewAgent | null>(agent);
  const {toastSuccess, toastError, toastWarning} = useToast();
  const permissionsMap = useUserStore((state) => state.permissionsMap);
  const {get: getAgentAbilityTypes, abilityTypes} = useGetAgentAbilityTypes();
  const {get: getAgentActionTypes, actionTypes} = useGetAgentActionTypes();
  const {getVoices, voices, isLoadingVoices} = useGetVoices();
  const flags = useFlags<FeatureFlagSet>();

  useEffect(() => {
    if (agentId) {
      void fetch(agentId);
    } else {
      navigate(ROUTES.AGENTS_LIST);
    }
  }, [agentId, fetch, navigate]);

  useEffect(() => {
    void getAgentAbilityTypes();
    void getAgentActionTypes();
    void getVoices();
  }, []);

  useEffect(() => {
    if (agent) {
      setLocalAgent(agent);
    }
  }, [agent]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleAgentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (localAgent) {
      setLocalAgent({...localAgent, [e.target.name]: e.target.value});
    }
  };

  const handleVoiceChange = (voiceId: string | undefined) => {
    if (localAgent) {
      const voice = voices?.find((v) => v.voiceId === voiceId);
      const provider = voice?.voiceProvider;
      setLocalAgent({...localAgent, voiceId: voiceId, voiceProvider: provider});
    }
  };

  const handleSave = async () => {
    if (!localAgent) return;
    try {
      await update(localAgent);
      toastSuccess('Agent saved successfully.');
    } catch (e) {
      toastError('Failed to save agent. Please try again.');
    }
  };

  const handleDelete = async () => {
    try {
      await del((localAgent as Agent).agentId);
      navigate(ROUTES.AGENTS_LIST);
      toastWarning('Agent deleted.');
    } catch (e) {
      toastError('Failed to delete agent. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.AGENTS_LIST);
  };

  const isNameInvalid = useMemo(() => !localAgent?.name.trim(), [localAgent?.name]);
  const isPersonaInvalid = useMemo(() => !localAgent?.persona.trim(), [localAgent?.persona]);
  const isGreetingInvalid = useMemo(() => !localAgent?.greeting.trim(), [localAgent?.greeting]);
  const isFormInvalid = isNameInvalid || isPersonaInvalid || isGreetingInvalid;
  const canDelete = permissionsMap[PERMISSIONS.TRUSSTED_AGENT_DELETE];

  if (isLoadingGet && !localAgent) {
    return (
      <Stack alignItems={'center'} justifyContent={'center'} height={'100%'}>
        <CircularProgress />
        <Typography sx={{mt: 2}}>Loading Agent...</Typography>
      </Stack>
    );
  }

  if (errorGet || (!localAgent && !isLoadingGet)) {
    return (
      <Stack alignItems={'center'} justifyContent={'center'} height={'100%'} spacing={2}>
        <Typography variant={'h5'}>{errorGet || `Agent with ID "${agentId}" does not exist.`}</Typography>
        <Button variant={'contained'} onClick={() => navigate(ROUTES.AGENTS_LIST)}>
          Go Back
        </Button>
      </Stack>
    );
  }

  if (!localAgent) {
    return (
      <Stack alignItems={'center'} justifyContent={'center'} height={'100%'}>
        <CircularProgress />
        <Typography sx={{mt: 2}}>Loading Agent...</Typography>
      </Stack>
    );
  }

  return (
    <PageContainer
      title={'Edit Agent'}
      subtitle={
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          <Typography color={'text.secondary'} variant={'subtitle2'}>
            {localAgent?.name || 'Agent'}
          </Typography>

          {localAgent?.isDefault && (
            <Chip label={'Default'} color={'success'} size={'small'} sx={{alignSelf: 'flex-start'}} />
          )}
        </Stack>
      }
      actions={
        <Stack direction={'row'} spacing={2}>
          <Button
            variant={'text'}
            color={'secondary'}
            onClick={handleCancel}
            disabled={isLoadingUpdate || isLoadingDelete}
          >
            Cancel
          </Button>
          <Button
            variant={'contained'}
            color={'primary'}
            onClick={handleSave}
            disabled={isFormInvalid || isLoadingUpdate || isLoadingDelete}
            startIcon={<SaveIcon />}
            loading={isLoadingUpdate || isLoadingDelete}
          >
            Save
          </Button>
        </Stack>
      }
      compact
      stack={false}
    >
      <TabContext value={tabValue}>
        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
          <TabList onChange={handleTabChange}>
            <Tab label={'About'} value={'about'} icon={<InfoIcon />} iconPosition={'start'} />
            <Tab label={'Abilities'} value={'abilities'} icon={<BuildIcon />} iconPosition={'start'} />
            {flags[FeatureFlags.featureAgentFaq] && (
              <Tab label={'FAQs'} value={'faqs'} icon={<QuestionAnswerIcon />} iconPosition={'start'} />
            )}
            <Tab label={'Documents'} value={'documents'} icon={<DescriptionIcon />} iconPosition={'start'} />
            <Tab label={'Settings'} value={'settings'} icon={<SettingsIcon />} iconPosition={'start'} />
          </TabList>
        </Box>

        <TabPanel value={'about'}>
          <AgentAbout
            formValues={localAgent}
            handleVoiceChange={handleVoiceChange}
            handleAgentChange={handleAgentChange}
            isNameInvalid={isNameInvalid}
            isPersonaInvalid={isPersonaInvalid}
            isGreetingInvalid={isGreetingInvalid}
            voices={voices}
            isLoadingVoices={isLoadingVoices}
          />
        </TabPanel>

        <TabPanel value={'abilities'}>
          <AgentAbilities
            abilities={localAgent?.abilities || []}
            setAbilities={(abilities) => setLocalAgent({...localAgent, abilities: abilities})}
            abilityTypes={abilityTypes}
            actionTypes={actionTypes}
          />
        </TabPanel>

        <TabPanel value={'faqs'}>
          {flags[FeatureFlags.featureAgentFaq] && (
            <AgentFAQs
              faqs={(localAgent?.faqs || []).map((faq) => ({
                ...faq,
                questionId: faq.questionId || uuidv4(),
              }))}
              setFaqs={(faqs) => setLocalAgent({...localAgent, faqs})}
            />
          )}
        </TabPanel>

        <TabPanel value={'documents'}>
          <AgentDocuments isSaved={true} agentId={agentId} />
        </TabPanel>

        <TabPanel value={'settings'}>
          <AgentSettings
            agentId={agentId}
            isDefault={localAgent?.isDefault || false}
            setIsDefault={(isDefault) => setLocalAgent({...localAgent, isDefault})}
            onDelete={handleDelete}
            canDelete={canDelete}
            agentPhoneNumbers={localAgent?.phoneNumbers}
            onPhoneNumbersChange={(phoneNumbers) => setLocalAgent({...localAgent, phoneNumbers})}
          />
        </TabPanel>
      </TabContext>
    </PageContainer>
  );
};

export default AgentsEditPage;
